<template>
  <div
    class="vld-parent crew-overview"
    :class="{ 'p-4': isDisplayMode }"
  >

    <b-row
      v-if="!isDisplayMode"
      class="align-items-center mb-4"
    >
      <b-col md="5">
        <h4 class="font-weight-bold ml-1">Infoskjerm</h4>
      </b-col>
      <b-col
        md="7"
        class="text-right"
      >
        <b-button
          variant="outline-primary"
          class="mr-2"
          @click="$bvModal.show('displaySettingsModal')"
        ><i class="ion ion-ios-settings mr-1"></i>Innstillinger</b-button>

        <b-button
          variant="primary"
          @click="openTvMode"
        ><i class="ion ion-ios-desktop mr-1"></i>Åpne infoskjerm</b-button>
      </b-col>
    </b-row>

    <b-modal
      id="displaySettingsModal"
      title="Innstillinger"
      hide-footer
    >
      <div class="form-group">
        <label for="tv-refresh-interval">Oppdater skjerm hvert</label>
        <div class="d-flex align-items-center">
          <b-input
            id="tv-refresh-interval"
            v-model.number="refreshMinutes"
            type="number"
            min="1"
            step="1"
            class="mr-2"
            style="width: 90px"
          ></b-input>
          <span class="text-muted mr-3">min.</span>
          <b-button
            v-if="!isAutoUpdating"
            variant="outline-success"
            size="sm"
            class="mr-1"
            title="Start automatisk oppdatering"
            @click="startAutoUpdate"
          ><i class="ion ion-ios-play mr-1"></i>Start</b-button>
          <b-button
            v-else
            variant="outline-danger"
            size="sm"
            class="mr-1"
            title="Stopp automatisk oppdatering"
            @click="stopAutoUpdate"
          ><i class="ion ion-ios-square mr-1"></i>Stopp</b-button>
        </div>
      </div>

      <div class="form-group">
        <label for="tv-vessels-per-row">Fartøy pr. rad</label>
        <div class="d-flex align-items-center">
          <b-input
            id="tv-vessels-per-row"
            v-model.number="vesselsPerRow"
            type="number"
            min="1"
            max="12"
            step="1"
            class="mr-2"
            style="width: 90px"
          ></b-input>
          <span class="text-muted">fartøy</span>
        </div>
      </div>

      <hr />

      <div class="form-group mb-0">
        <label>Lenke til skjermvisning</label>
        <p class="text-muted small">
          Lenken logger skjermen inn automatisk og åpner siden i visningsmodus. Del den kun med skjermen den skal vises på.
        </p>
        <b-input-group>
          <b-form-input
            readonly
            :value="displayLink"
            @focus="$event.target.select()"
          ></b-form-input>
          <b-input-group-append>
            <b-button
              variant="outline-primary"
              @click="copyDisplayLink"
            ><i class="ion ion-ios-copy mr-1"></i>Kopier</b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
    </b-modal>

    <!-- Modal: Edit crew member -->
    <b-modal
      id="crewOverviewEditCrew"
      title="Rediger mannskap"
    >
      <b-form-group label="Navn">
        <b-form-input
          v-model="crewEdit.name"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Rolle">
        <b-form-select
          v-model="crewEdit.role"
          :options="roleOptions"
        ></b-form-select>
      </b-form-group>

      <b-form-group label="Fartøy">
        <b-form-select
          v-model="crewEdit.vessel"
          :options="vesselOptions"
        ></b-form-select>
      </b-form-group>

      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="outline-danger"
            class="float-left"
            @click="deleteCrewEdit"
          >Slett</b-button>

          <b-button
            variant="primary"
            class="float-right"
            @click="saveCrewEdit"
          >Lagre</b-button>

          <b-button
            variant="default"
            class="float-right mr-2"
            @click="$bvModal.hide('crewOverviewEditCrew')"
          >Avbryt</b-button>
        </div>
      </template>
    </b-modal>

    <!-- Modal: Edit skift -->
    <b-modal
      id="crewOverviewEditSkift"
      title="Rediger skift"
      size="lg"
    >
      <b-form-group label="Navn">
        <b-form-input
          v-model="skiftEdit.name"
          required
        ></b-form-input>
      </b-form-group>

      <b-form-group label="Fartøy">
        <b-form-select
          v-model="skiftEdit.vessel"
          :options="vesselOptions"
        ></b-form-select>
      </b-form-group>

      <hr />

      <b-form-group label="Mannskap i dette skiftet">
        <div style="max-height: 320px; overflow-y: auto;">
          <div
            v-for="member in crews"
            :key="member.id"
            class="member-card mb-2 p-2 border rounded d-flex align-items-center"
            :class="{ 'member-card-selected': isCrewInSkiftEdit(member) }"
            @click="toggleCrewSkiftEdit(member)"
          >
            <i
              class="ion mr-2"
              :class="isCrewInSkiftEdit(member) ? 'ion-ios-checkmark-circle text-success' : 'ion-ios-radio-button-off text-muted'"
            ></i>
            <span class="flex-grow-1">
              {{ member.name }}
              <span
                v-if="member.vessel"
                class="text-muted small"
              >({{ member.vessel.name }})</span>
            </span>
            <b-badge
              pill
              :variant="roleVariant(member.role)"
            >{{ member.role || 'Ukjent' }}</b-badge>
          </div>
          <p
            v-if="crews.length === 0"
            class="text-muted small"
          >Ingen mannskap tilgjengelig</p>
        </div>
      </b-form-group>

      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="outline-danger"
            class="float-left"
            @click="deleteSkiftEdit"
          >Slett</b-button>

          <b-button
            variant="primary"
            class="float-right"
            @click="saveSkiftEdit"
          >Lagre</b-button>

          <b-button
            variant="default"
            class="float-right mr-2"
            @click="$bvModal.hide('crewOverviewEditSkift')"
          >Avbryt</b-button>
        </div>
      </template>
    </b-modal>

    <div
      v-if="isDisplayMode"
      class="tv-controls"
    >
      <b-button
        variant="light"
        class="tv-back-btn"
        title="Tilbake til redigering"
        @click="$router.push('/dashboard/crew-overview')"
      ><i class="ion ion-ios-arrow-back"></i></b-button>

      <b-button
        variant="light"
        class="tv-refresh-btn"
        title="Oppdater nå"
        @click="manualRefresh"
      ><i class="ion ion-ios-refresh"></i></b-button>

      <div class="tv-progress-wrap">
        <svg
          v-if="isAutoUpdating"
          class="tv-progress-ring"
          viewBox="0 0 72 72"
        >
          <circle
            class="tv-progress-ring-bg"
            cx="36"
            cy="36"
            r="32"
          />
          <circle
            class="tv-progress-ring-fg"
            cx="36"
            cy="36"
            r="32"
            :stroke-dasharray="ringCircumference"
            :stroke-dashoffset="ringOffset"
          />
        </svg>

        <b-button
          v-if="!isAutoUpdating"
          variant="light"
          title="Start automatisk oppdatering"
          @click="startAutoUpdate"
        ><i class="ion ion-ios-play"></i></b-button>
        <b-button
          v-else
          variant="light"
          title="Stopp automatisk oppdatering"
          @click="stopAutoUpdate"
        ><i class="ion ion-ios-square"></i></b-button>
      </div>

      <b-button
        variant="light"
        class="tv-fullscreen-btn"
        :title="isFullscreen ? 'Avslutt fullskjerm' : 'Fullskjerm'"
        @click="toggleFullscreen"
      ><i :class="isFullscreen ? 'ion ion-ios-contract' : 'ion ion-ios-expand'"></i></b-button>
    </div>

    <div
      v-if="isDisplayMode"
      class="tv-clock-container"
    >
      <div class="tv-clock-wrap">
        <div class="tv-clock">{{ currentTimeDisplay }}</div>
        <div
          v-if="lastUpdated"
          class="tv-last-updated"
        >Sist oppdatert: {{ lastUpdatedDisplay }}</div>
      </div>
    </div>

    <router-link
      v-if="isDisplayMode"
      to="/dashboard/crew-overview"
      class="tv-logo"
    >
      <img
        :src="`${publicUrl}img/logo/eines-logo.svg`"
        alt
      />
    </router-link>

    <div
      v-if="isLoading"
      class="text-center my-5"
    >
      <b-spinner variant="primary"></b-spinner>
    </div>

    <b-row
      v-else
      class="mt-2"
    >
      <!-- Main panel: one card per vessel -->
      <b-col
        :md="isDisplayMode ? 12 : 9"
        class="pr-4"
        :class="{ 'vessel-main-pane': !isDisplayMode }"
      >
        <h6 v-if="shownVessels.length === 0">Ingen fartøy tilgjengelig</h6>

        <draggable
          v-model="shownVessels"
          group="vessel-visibility"
          handle=".vessel-drag-handle"
          :disabled="isDisplayMode"
          tag="b-row"
          class="align-items-start"
          @change="onVesselVisibilityChange($event, true)"
        >
          <b-col
            :style="vesselColStyle"
            v-for="vessel in shownVessels"
            :key="vessel.id"
            class="mb-3"
          >
            <b-card header-class="font-weight-bold">
              <template #header>
                <div class="d-flex align-items-center">
                  <i
                    v-if="!isDisplayMode"
                    class="ion ion-ios-reorder vessel-drag-handle mr-2"
                    title="Dra for å skjule fra infoskjerm"
                  ></i>
                  <span>{{ vessel.name }}</span>
                </div>
              </template>

              <!-- Active order -->
              <div class="dropzone-wrap">
                <draggable
                  v-model="orderGroups[vessel.id]"
                  group="order-assign"
                  handle=".drag-handle"
                  :disabled="isDisplayMode"
                  :class="['resource-dropzone', { 'is-empty': orderGroups[vessel.id] && orderGroups[vessel.id].length === 0 }]"
                  tag="div"
                  @change="onOrderChange($event, vessel)"
                >
                  <div
                    v-for="order in orderGroups[vessel.id]"
                    :key="order.id"
                    class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
                  >
                    <i
                      v-if="!isDisplayMode"
                      class="ion ion-ios-reorder drag-handle mr-2"
                    ></i>
                    <router-link
                      :to="{ name: 'order-edit', params: { id: order.id } }"
                      class="flex-grow-1"
                    >{{ order.ordernumber }}<template v-if="order.customer_company"> - {{ order.customer_company.name }}</template></router-link>
                    <b-badge
                      pill
                      :variant="statusVariant(order.status)"
                    >{{ order.status }}</b-badge>
                  </div>
                </draggable>
                <p
                  v-if="orderGroups[vessel.id] && orderGroups[vessel.id].length === 0"
                  class="empty-placeholder text-muted small mb-0"
                >Ingen aktiv ordre</p>
              </div>
              <hr class="vessel-divider" />

              <!-- Mannskap: skift på vakt (grouped) plus individuelt
                   tildelt mannskap, combined in one section. -->
              <div class="dropzone-wrap">
                <draggable
                  v-if="skifts.length > 0"
                  v-model="activeSkiftGroups[vessel.id]"
                  group="skift-duty"
                  handle=".skift-drag-handle"
                  :disabled="isDisplayMode"
                  :class="['resource-dropzone', { 'is-empty': activeSkiftGroups[vessel.id] && activeSkiftGroups[vessel.id].length === 0 }]"
                  tag="div"
                  @change="onSkiftActiveChange($event, vessel)"
                >
                  <div
                    v-for="skift in activeSkiftGroups[vessel.id]"
                    :key="skift.id"
                    class="mb-2"
                  >
                    <div
                      class="resource-chip p-2 border rounded d-flex align-items-center skift-drag-handle"
                      title="Dra tilbake til ressurser"
                    >
                      <i
                        v-if="!isDisplayMode"
                        class="ion ion-ios-reorder mr-2"
                      ></i>
                      <span
                        class="flex-grow-1"
                        :class="{ 'entity-name-link': !isDisplayMode }"
                        @click="openEditSkift(skift)"
                      >{{ skift.name }}</span>
                      <b-badge
                        pill
                        variant="light"
                        class="mr-1 skift-count-badge"
                      >{{ skiftMemberCount(skift) }}</b-badge>
                      <b-badge
                        pill
                        variant="info"
                        class="mr-2"
                      >På vakt</b-badge>
                      <i
                        v-if="!isDisplayMode"
                        class="ion skift-toggle"
                        :class="isSkiftExpanded(skift) ? 'ion-ios-arrow-up' : 'ion-ios-arrow-down'"
                        title="Vis/skjul mannskap"
                        @click.stop="toggleSkift(skift.id)"
                      ></i>
                    </div>
                    <div
                      v-if="(skiftGroups[skift.id] || []).length && isSkiftExpanded(skift)"
                      class="pl-3 mt-2"
                    >
                      <div
                        v-for="member in skiftGroups[skift.id]"
                        :key="member.id"
                        class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
                      >
                        <span
                          class="flex-grow-1"
                          :class="{ 'entity-name-link': !isDisplayMode }"
                          @click="openEditCrew(member)"
                        >{{ member.name }}</span>
                        <b-badge
                          pill
                          :variant="roleVariant(member.role)"
                        >{{ member.role || 'Ukjent' }}</b-badge>
                      </div>
                    </div>
                  </div>
                </draggable>
                <draggable
                  v-model="crewGroups[vessel.id]"
                  group="crew"
                  handle=".drag-handle"
                  :disabled="isDisplayMode"
                  :class="['resource-dropzone', { 'is-empty': crewGroups[vessel.id] && crewGroups[vessel.id].length === 0 }]"
                  tag="div"
                  @change="onCrewChange($event, vessel)"
                >
                  <div
                    v-for="member in crewGroups[vessel.id]"
                    :key="member.id"
                    class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
                  >
                    <i
                      v-if="!isDisplayMode"
                      class="ion ion-ios-reorder drag-handle mr-2"
                    ></i>
                    <span
                      class="flex-grow-1"
                      :class="{ 'entity-name-link': !isDisplayMode }"
                      @click="openEditCrew(member)"
                    >{{ member.name }}</span>
                    <b-badge
                      pill
                      :variant="roleVariant(member.role)"
                    >{{ member.role || 'Ukjent' }}</b-badge>
                  </div>
                </draggable>
                <p
                  v-if="(crewGroups[vessel.id] || []).length === 0 && (activeSkiftGroups[vessel.id] || []).length === 0"
                  class="empty-placeholder text-muted small mb-0"
                >Ingen mannskap tildelt</p>
              </div>
              <hr class="vessel-divider" />

              <!-- Turrapport -->
              <div class="dropzone-wrap">
                <draggable
                  v-model="tripGroups[vessel.id]"
                  group="trip-assign"
                  handle=".drag-handle"
                  :disabled="isDisplayMode"
                  :class="['resource-dropzone', { 'is-empty': tripGroups[vessel.id] && tripGroups[vessel.id].length === 0 }]"
                  tag="div"
                  @change="onTripChange($event, vessel)"
                >
                  <div
                    v-for="entry in tripGroups[vessel.id]"
                    :key="entry.id"
                    class="resource-chip trip-chip mb-2 p-2 border rounded"
                  >
                    <div
                      v-for="(trip, index) in entry.trips"
                      :key="trip.id"
                      class="trip-entry"
                    >
                      <div
                        class="d-flex align-items-center trip-toggle"
                        @click="toggleTrip(trip.id)"
                      >
                        <i
                          v-if="index === 0 && !isDisplayMode"
                          class="ion ion-ios-reorder drag-handle mr-2"
                        ></i>
                        <router-link
                          :to="{ name: 'order-edit', params: { id: entry.order.id } }"
                          class="flex-grow-1"
                        >Turrapport {{ trip.header && trip.header.tripNumber }}</router-link>
                        <small
                          v-if="tripDuration(trip)"
                          class="text-muted mr-1"
                        ><i class="ion ion-ios-timer mr-1"></i>{{ tripDuration(trip) }}</small>
                        <i :class="['ion ml-1', expandedTrips[trip.id] ? 'ion-ios-arrow-down' : 'ion-ios-arrow-forward']"></i>
                      </div>

                      <div
                        v-if="expandedTrips[trip.id]"
                        class="trip-details small text-muted mt-1 mb-2"
                      >
                        <div class="font-weight-bold">Lasting</div>
                        <div class="d-flex justify-content-between">
                          <span>Ankomst lasteplass:</span>
                          <span>{{ formatStep(trip.loading.arrivalDate, trip.loading.arrivalTime) }}</span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Lasting startet:</span>
                          <span>{{ formatStep(trip.loading.startDate, trip.loading.startTime) }}</span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Lasting avsluttet:</span>
                          <span>{{ formatStep(trip.loading.endDate, trip.loading.endTime) }}</span>
                        </div>

                        <div class="font-weight-bold mt-2">Reise</div>
                        <div class="d-flex justify-content-between">
                          <span>Avreise tidspunkt:</span>
                          <span>{{ formatStep(trip.travel.recordedDate, trip.travel.recordedTime) }}</span>
                        </div>

                        <div class="font-weight-bold mt-2">Lossing</div>
                        <div class="d-flex justify-content-between">
                          <span>Ankomst losseplass:</span>
                          <span>{{ formatStep(trip.unloading.arrivalDate, trip.unloading.arrivalTime) }}</span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Start lossing:</span>
                          <span>{{ formatStep(trip.unloading.startDate, trip.unloading.startTime) }}</span>
                        </div>
                        <div class="d-flex justify-content-between">
                          <span>Ferdig losset:</span>
                          <span>{{ formatStep(trip.unloading.endDate, trip.unloading.endTime) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </draggable>
                <p
                  v-if="tripGroups[vessel.id] && tripGroups[vessel.id].length === 0"
                  class="empty-placeholder text-muted small mb-0"
                >Ingen turrapport</p>
              </div>
            </b-card>
          </b-col>
        </draggable>
      </b-col>

      <!-- Resource pane -->
      <b-col
        v-if="!isDisplayMode"
        md="3"
        class="border-left pl-5 resource-pane"
      >
        <b-card
          header="Mannskap"
          header-class="font-weight-bold"
          class="mb-3"
        >
          <!-- Tree: skift first, mannskap nested under. Only off-duty
               (inactive) skift show here - drag one onto a vessel's "Skift
               på vakt" section to put it on duty; it moves back here (and
               can be reused) when dragged off again. -->
          <draggable
            v-model="inactiveSkifts"
            group="skift-duty"
            handle=".skift-drag-handle"
            :class="['resource-dropzone', { 'is-empty': inactiveSkifts.length === 0 }]"
            tag="div"
            @change="onSkiftActiveChange($event, null)"
          >
            <div
              v-for="skift in inactiveSkifts"
              :key="skift.id"
              class="mb-3"
            >
              <div
                class="d-flex align-items-center mb-1 skift-drag-handle"
                title="Dra for å sette hele skiftet på vakt på et fartøy"
              >
                <i class="ion ion-ios-reorder mr-1"></i>
                <i class="ion ion-ios-albums text-muted mr-1"></i>
                <strong
                  class="small text-uppercase entity-name-link"
                  @click="openEditSkift(skift)"
                >{{ skift.name }}</strong>
                <span
                  v-if="skift.vessel"
                  class="text-muted small ml-1"
                >({{ skift.vessel.name }})</span>
                <b-badge
                  pill
                  variant="light"
                  class="ml-2 skift-count-badge"
                >{{ skiftMemberCount(skift) }}</b-badge>
                <i
                  class="ion skift-toggle ml-auto"
                  :class="isSkiftExpanded(skift) ? 'ion-ios-arrow-up' : 'ion-ios-arrow-down'"
                  title="Vis/skjul mannskap"
                  @click.stop="toggleSkift(skift.id)"
                ></i>
              </div>
              <template v-if="isSkiftExpanded(skift)">
                <draggable
                  v-model="skiftGroups[skift.id]"
                  group="crew"
                  handle=".drag-handle"
                  class="resource-dropzone pl-3"
                  tag="div"
                  @change="onCrewChange($event, null)"
                >
                  <div
                    v-for="member in skiftGroups[skift.id]"
                    :key="member.id"
                    class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
                  >
                    <i class="ion ion-ios-reorder drag-handle mr-2"></i>
                    <span
                      class="flex-grow-1 entity-name-link"
                      @click="openEditCrew(member)"
                    >{{ member.name }}</span>
                    <b-badge
                      pill
                      :variant="roleVariant(member.role)"
                    >{{ member.role || 'Ukjent' }}</b-badge>
                  </div>
                </draggable>
                <p
                  v-if="(skiftGroups[skift.id] || []).length === 0"
                  class="text-muted small pl-3 mb-0"
                >Ingen mannskap i dette skiftet</p>
              </template>
            </div>
          </draggable>
          <p
            v-if="inactiveSkifts.length === 0 && skifts.length > 0"
            class="text-muted small mb-3"
          >Alle skift er på vakt</p>

          <div
            v-if="skifts.length > 0"
            class="d-flex align-items-center mb-1"
          >
            <i class="ion ion-ios-albums text-muted mr-1"></i>
            <strong class="small text-uppercase">Uten skift</strong>
            <b-badge
              pill
              variant="light"
              class="ml-2"
            >{{ unassignedCrewNoSkift.length }}</b-badge>
            <i
              class="ion skift-toggle ml-auto"
              :class="isUnassignedCrewExpanded ? 'ion-ios-arrow-up' : 'ion-ios-arrow-down'"
              title="Vis/skjul mannskap"
              @click.stop="toggleUnassignedCrew"
            ></i>
          </div>
          <template v-if="skifts.length === 0 || isUnassignedCrewExpanded">
            <draggable
              v-model="unassignedCrewNoSkift"
              group="crew"
              handle=".drag-handle"
              class="resource-dropzone"
              :class="{ 'pl-3': skifts.length > 0 }"
              tag="div"
              @change="onCrewChange($event, null)"
            >
              <div
                v-for="member in unassignedCrewNoSkift"
                :key="member.id"
                class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
              >
                <i class="ion ion-ios-reorder drag-handle mr-2"></i>
                <span
                  class="flex-grow-1 entity-name-link"
                  @click="openEditCrew(member)"
                >{{ member.name }}</span>
                <b-badge
                  pill
                  :variant="roleVariant(member.role)"
                >{{ member.role || 'Ukjent' }}</b-badge>
              </div>
            </draggable>
            <p
              v-if="unassignedCrewNoSkift.length === 0"
              class="text-muted small mb-0"
            >{{ skifts.length > 0 ? "Alle er tildelt et skift eller fartøy" : "Alle er tildelt et fartøy" }}</p>
          </template>
        </b-card>

        <b-card
          header="Aktive ordrer"
          header-class="font-weight-bold"
          class="mb-3"
        >
          <draggable
            v-model="unassignedOrders"
            group="order-assign"
            handle=".drag-handle"
            class="resource-dropzone"
            tag="div"
            @change="onOrderChange($event, null)"
          >
            <div
              v-for="order in unassignedOrders"
              :key="order.id"
              class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
            >
              <i class="ion ion-ios-reorder drag-handle mr-2"></i>
              <router-link
                :to="{ name: 'order-edit', params: { id: order.id } }"
                class="flex-grow-1"
              >{{ order.ordernumber }}<template v-if="order.customer_company"> - {{ order.customer_company.name }}</template></router-link>
              <b-badge
                pill
                :variant="statusVariant(order.status)"
              >{{ order.status }}</b-badge>
            </div>
          </draggable>
          <p
            v-if="unassignedOrders.length === 0"
            class="text-muted small mb-0"
          >Ingen ordre uten fartøy</p>
        </b-card>

        <b-card
          header="Turrapporter"
          header-class="font-weight-bold"
          class="mb-3"
        >
          <draggable
            v-model="unassignedTrips"
            group="trip-assign"
            handle=".drag-handle"
            class="resource-dropzone"
            tag="div"
            @change="onTripChange($event, null)"
          >
            <div
              v-for="entry in unassignedTrips"
              :key="entry.id"
              class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
            >
              <i class="ion ion-ios-reorder drag-handle mr-2"></i>
              <router-link
                :to="{ name: 'order-edit', params: { id: entry.order.id } }"
                class="flex-grow-1"
              ><i class="ion ion-ios-clipboard mr-1"></i>{{ entry.order.ordernumber }}<template v-if="entry.order.customer_company"> - {{ entry.order.customer_company.name }}</template></router-link>
            </div>
          </draggable>
          <p
            v-if="unassignedTrips.length === 0"
            class="text-muted small mb-0"
          >Ingen turrapporter uten fartøy</p>
        </b-card>

        <b-card
          header="Fartøy"
          header-class="font-weight-bold"
        >
          <p class="text-muted small">Dra et fartøy til hovedområdet for å vise det på infoskjerm.</p>
          <draggable
            v-model="hiddenVessels"
            group="vessel-visibility"
            handle=".drag-handle"
            class="resource-dropzone"
            tag="div"
            @change="onVesselVisibilityChange($event, false)"
          >
            <div
              v-for="vessel in hiddenVessels"
              :key="vessel.id"
              class="resource-chip mb-2 p-2 border rounded d-flex align-items-center"
            >
              <i class="ion ion-ios-reorder drag-handle mr-2"></i>
              <span class="flex-grow-1">{{ vessel.name }}</span>
            </div>
          </draggable>
          <p
            v-if="hiddenVessels.length === 0"
            class="text-muted small mb-0"
          >Alle fartøy vises på infoskjerm</p>
        </b-card>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import axios from "axios";
import draggable from "vuedraggable";

export default {
  components: {
    draggable,
  },
  data() {
    return {
      vessels: [],
      shownVessels: [],
      hiddenVessels: [],
      crews: [],
      orders: [],
      skifts: [],
      crewGroups: {},
      unassignedCrew: [],
      skiftGroups: {},
      unassignedCrewNoSkift: [],
      activeSkiftGroups: {},
      inactiveSkifts: [],
      orderGroups: {},
      unassignedOrders: [],
      tripGroups: {},
      unassignedTrips: [],
      expandedTrips: {},
      expandedSkifts: {},
      expandedUnassignedCrew: false,
      crewEdit: {},
      skiftEdit: {},
      isLoading: false,
      now: Date.now(),
      lastUpdated: null,
      isFullscreen: false,
      clockInterval: null,
      refreshMinutes: 5,
      vesselsPerRow: 4,
      isAutoUpdating: false,
      nextUpdateAt: null,
      roleVariants: {
        "Skipper": "danger",
        "Styrmann": "warning",
        "Båtfører": "success",
        "Maskinist": "primary",
        "Motormann": "dark",
        "Matros": "info",
        "Dekksmann": "secondary",
        "Kokk": "warning",
        "Operatør": "primary",
        "Kadett": "secondary",
        "Lærling": "secondary",
        "Maskinpasser": "dark",
        "Matros/Kokk": "info",
        "Flåteansvarlig": "success",
      },
      statusVariants: {
        "Bestilling": "secondary",
        "Ordre": "primary",
        "I arbeid": "dark",
        "Arbeid avsluttet": "warning",
        "Til fakturering": "info",
        "Fakturert": "success",
        "Utført": "success",
        "Kansellert": "danger",
      },
    };
  },
  computed: {
    isDisplayMode() {
      return this.$route.query.display === "tv";
    },
    isUnassignedCrewExpanded() {
      return this.isDisplayMode || this.expandedUnassignedCrew;
    },
    safeRefreshMinutes() {
      var value = parseFloat(this.refreshMinutes);
      return isFinite(value) && value > 0 ? value : 5;
    },
    refreshIntervalMinutes() {
      var fromQuery = parseFloat(this.$route.query.interval);
      return isFinite(fromQuery) && fromQuery > 0 ? fromQuery : this.safeRefreshMinutes;
    },
    safeVesselsPerRow() {
      var value = parseInt(this.vesselsPerRow, 10);
      return isFinite(value) && value > 0 ? value : 4;
    },
    vesselsPerRowFromQuery() {
      var fromQuery = parseInt(this.$route.query.cols, 10);
      return isFinite(fromQuery) && fromQuery > 0 ? fromQuery : this.safeVesselsPerRow;
    },
    vesselColStyle() {
      // Same column count in the editing view and the TV display: in TV
      // mode the count travels via the "cols" query param (see displayLink/
      // openTvMode), in the editing view it reflects the settings input
      // directly since there's no query param to read there.
      var width = 100 / this.vesselsPerRowFromQuery;
      return {
        flex: "0 0 " + width + "%",
        maxWidth: width + "%",
      };
    },
    ringCircumference() {
      return 2 * Math.PI * 32;
    },
    autoUpdateProgress() {
      if (!this.isAutoUpdating || !this.nextUpdateAt) return 0;

      var intervalMs = this.refreshIntervalMinutes * 60000;
      var remaining = this.nextUpdateAt - this.now;
      var fraction = (intervalMs - remaining) / intervalMs;

      return Math.min(1, Math.max(0, fraction));
    },
    ringOffset() {
      return this.ringCircumference * (1 - this.autoUpdateProgress);
    },
    currentTimeDisplay() {
      return this.$date(this.now).format("HH:mm");
    },
    lastUpdatedDisplay() {
      if (!this.lastUpdated) return "";
      // Depend on `now` (ticks every second) so this stays live between
      // refreshes instead of freezing at whatever it said right after load.
      var keepLive = this.now;
      return this.$date(this.lastUpdated).fromNow();
    },
    roleOptions() {
      return [{ value: null, text: "Ingen rolle" }].concat(
        Object.keys(this.roleVariants).map((role) => ({ value: role, text: role }))
      );
    },
    vesselOptions() {
      return [{ value: null, text: "Ingen" }].concat(
        this.vessels.map((vessel) => ({ value: vessel, text: vessel.name }))
      );
    },
    displayLink() {
      var route = this.$router.resolve({
        path: this.$route.path,
        query: {
          token: localStorage.getItem("token"),
          company: localStorage.getItem("company"),
          display: "tv",
          interval: this.safeRefreshMinutes,
          cols: this.safeVesselsPerRow,
        },
      });

      return window.location.origin + route.href;
    },
  },
  methods: {
    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.log(error);
        });
      } else {
        document.exitFullscreen();
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    openTvMode() {
      var route = this.$router.resolve({
        path: this.$route.path,
        query: { display: "tv", interval: this.safeRefreshMinutes, cols: this.safeVesselsPerRow },
      });
      window.open(route.href, "_blank");
    },
    copyDisplayLink() {
      // Avoid $copyText/clipboard.js here - it copies via a hidden,
      // temporarily-focused textarea, which races with this button's
      // parent b-modal focus trap (BootstrapVue snaps focus back into the
      // modal as soon as it leaves), making the copy succeed or fail
      // unpredictably. The native Clipboard API writes directly without
      // moving focus, so it isn't affected.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(this.displayLink)
          .then(this.onDisplayLinkCopied)
          .catch(this.onDisplayLinkCopyFailed);
      } else {
        this.$copyText(this.displayLink)
          .then(this.onDisplayLinkCopied)
          .catch(this.onDisplayLinkCopyFailed);
      }
    },
    onDisplayLinkCopied() {
      this.$bvToast.toast(
        "Lenken gir full tilgang uten innlogging - del den kun med skjermen den skal vises på.",
        {
          title: "Skjermlenke kopiert",
          variant: "success",
          solid: true,
        }
      );
    },
    onDisplayLinkCopyFailed() {
      this.$bvToast.toast("Kunne ikke kopiere lenken.", {
        title: "Feil",
        variant: "danger",
        solid: true,
      });
    },
    startAutoUpdate() {
      this.isAutoUpdating = true;
      this.nextUpdateAt = Date.now() + this.refreshIntervalMinutes * 60000;
    },
    stopAutoUpdate() {
      this.isAutoUpdating = false;
      this.nextUpdateAt = null;
    },
    // A long setInterval is unreliable here: browsers throttle (or fully
    // suspend) timers in tabs that lose visibility/focus, which is exactly
    // where a TV display sits for long stretches - a scheduled refresh can
    // end up delayed by many minutes. Instead this is driven off the
    // 1-second clock tick (and visibilitychange) by comparing wall-clock
    // time against nextUpdateAt, so a delayed tick still catches up
    // immediately instead of missing the refresh entirely.
    checkAutoUpdateDue() {
      if (!this.isAutoUpdating || !this.nextUpdateAt) return;
      if (Date.now() < this.nextUpdateAt) return;

      this.loadData();
      this.nextUpdateAt = Date.now() + this.refreshIntervalMinutes * 60000;
    },
    onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      this.now = Date.now();
      this.checkAutoUpdateDue();
    },
    manualRefresh() {
      this.loadData();

      // Push the auto-update countdown back out so it doesn't fire again
      // right on the heels of this manual refresh.
      if (this.isAutoUpdating) {
        this.nextUpdateAt = Date.now() + this.refreshIntervalMinutes * 60000;
      }
    },
    loadData() {
      this.isLoading = true;
      var company = localStorage.getItem("company");

      Promise.all([
        axios.get("https://api.eines-as.no/vessels?_where[company]=" + company),
        axios.get("https://api.eines-as.no/crews?_where[company]=" + company),
        axios.get("https://api.eines-as.no/orders?status=I arbeid&_where[supplier_company]=" + company),
        // Skift is fetched independently so a missing/not-yet-permissioned
        // endpoint can't block vessels/crew/orders from loading - it just
        // falls back to an empty list (crew show up under "Uten skift").
        axios
          .get("https://api.eines-as.no/skift?_where[company]=" + company)
          .catch((error) => {
            console.log(error);
            return { data: [] };
          }),
      ])
        .then(([vesselsRes, crewsRes, ordersRes, skiftRes]) => {
          this.vessels = vesselsRes.data;
          this.crews = crewsRes.data;
          this.orders = ordersRes.data;
          this.skifts = skiftRes.data;
          this.buildGroups();
          this.lastUpdated = Date.now();
          this.isLoading = false;
        })
        .catch((error) => {
          console.log(error);
          this.isLoading = false;
        });
    },
    buildGroups() {
      var crewGroups = {};
      var orderGroups = {};
      var tripGroups = {};
      this.vessels.forEach((vessel) => {
        crewGroups[vessel.id] = [];
        orderGroups[vessel.id] = [];
        tripGroups[vessel.id] = [];
      });

      var unassignedCrew = [];
      this.crews.forEach((crew) => {
        if (crew.vessel && crew.vessel.id && crewGroups[crew.vessel.id]) {
          crewGroups[crew.vessel.id].push(crew);
        } else {
          unassignedCrew.push(crew);
        }
      });

      // Crew with no vessel are grouped under their skift (a vessel's
      // rotating crew group) for the resource pane's tree view.
      var skiftGroups = {};
      this.skifts.forEach((skift) => {
        skiftGroups[skift.id] = [];
      });

      var unassignedCrewNoSkift = [];
      unassignedCrew.forEach((crew) => {
        if (crew.skift && crew.skift.id && skiftGroups[crew.skift.id]) {
          skiftGroups[crew.skift.id].push(crew);
        } else {
          unassignedCrewNoSkift.push(crew);
        }
      });

      // A skift is either "on duty" on its vessel's card, or resting (and
      // reusable) in the resource pane - never both at once.
      var activeSkiftGroups = {};
      this.vessels.forEach((vessel) => {
        activeSkiftGroups[vessel.id] = [];
      });

      var inactiveSkifts = [];
      this.skifts.forEach((skift) => {
        if (skift.active && skift.vessel && skift.vessel.id && activeSkiftGroups[skift.vessel.id]) {
          activeSkiftGroups[skift.vessel.id].push(skift);
        } else {
          inactiveSkifts.push(skift);
        }
      });

      var unassignedOrders = [];
      var unassignedTrips = [];
      this.orders.forEach((order) => {
        if (order.vessel && order.vessel.id && orderGroups[order.vessel.id]) {
          orderGroups[order.vessel.id].push(order);
        } else {
          unassignedOrders.push(order);
        }

        if (order.type == "Brønnbåt" && order.trips && order.trips.length) {
          var latestTrip = order.trips.reduce((latest, trip) => {
            if (!latest) return trip;
            return new Date(trip.createdAt) > new Date(latest.createdAt) ? trip : latest;
          }, null);

          if (latestTrip && !(latestTrip.id in this.expandedTrips)) {
            this.$set(this.expandedTrips, latestTrip.id, true);
          }

          var entry = { id: order.id, trips: order.trips, order: order };

          if (order.vessel && order.vessel.id && tripGroups[order.vessel.id]) {
            tripGroups[order.vessel.id].push(entry);
          } else {
            unassignedTrips.push(entry);
          }
        }
      });

      this.crewGroups = crewGroups;
      this.unassignedCrew = unassignedCrew;
      this.skiftGroups = skiftGroups;
      this.unassignedCrewNoSkift = unassignedCrewNoSkift;
      this.activeSkiftGroups = activeSkiftGroups;
      this.inactiveSkifts = inactiveSkifts;
      this.orderGroups = orderGroups;
      this.unassignedOrders = unassignedOrders;
      this.tripGroups = tripGroups;
      this.unassignedTrips = unassignedTrips;

      // Vessels default to visible; only an explicit false hides them.
      this.shownVessels = this.vessels.filter((vessel) => vessel.show_on_infoscreen !== false);
      this.hiddenVessels = this.vessels.filter((vessel) => vessel.show_on_infoscreen === false);
    },
    onCrewChange(evt, vessel) {
      if (!evt.added) return;

      var crew = evt.added.element;
      var previousVessel = crew.vessel;
      crew.vessel = vessel;

      axios
        .put("https://api.eines-as.no/crews/" + crew.id, crew)
        .catch((error) => {
          console.log(error);
          crew.vessel = previousVessel;
          this.$bvToast.toast(
            "Kunne ikke flytte " + crew.name + ". Laster inn på nytt...",
            {
              title: "Feil",
              variant: "danger",
              solid: true,
            }
          );
          this.loadData();
        });
    },
    onSkiftActiveChange(evt, vessel) {
      if (!evt.added) return;

      var skift = evt.added.element;
      var previousActive = skift.active;
      var previousVessel = skift.vessel;

      skift.active = !!vessel;
      if (vessel) skift.vessel = vessel;

      axios
        .put("https://api.eines-as.no/skift/" + skift.id, {
          active: skift.active,
          vessel: skift.vessel ? skift.vessel.id : null,
        })
        .catch((error) => {
          console.log(error);
          skift.active = previousActive;
          skift.vessel = previousVessel;
          this.$bvToast.toast(
            "Kunne ikke flytte " + skift.name + ". Laster inn på nytt...",
            {
              title: "Feil",
              variant: "danger",
              solid: true,
            }
          );
          this.loadData();
        });
    },
    onOrderChange(evt, vessel) {
      if (!evt.added) return;

      var order = evt.added.element;
      var previousVessel = order.vessel;
      order.vessel = vessel;

      axios
        .put("https://api.eines-as.no/orders/" + order.id, {
          vessel: vessel ? vessel.id : null,
        })
        .then(() => {
          this.buildGroups();
        })
        .catch((error) => {
          console.log(error);
          order.vessel = previousVessel;
          this.$bvToast.toast(
            "Kunne ikke flytte ordre " + order.ordernumber + ". Laster inn på nytt...",
            {
              title: "Feil",
              variant: "danger",
              solid: true,
            }
          );
          this.loadData();
        });
    },
    onTripChange(evt, vessel) {
      if (!evt.added) return;

      var entry = evt.added.element;
      var order = entry.order;
      var previousVessel = order.vessel;
      order.vessel = vessel;

      axios
        .put("https://api.eines-as.no/orders/" + order.id, {
          vessel: vessel ? vessel.id : null,
        })
        .then(() => {
          this.buildGroups();
        })
        .catch((error) => {
          console.log(error);
          order.vessel = previousVessel;
          this.$bvToast.toast(
            "Kunne ikke flytte turrapport for ordre " + order.ordernumber + ". Laster inn på nytt...",
            {
              title: "Feil",
              variant: "danger",
              solid: true,
            }
          );
          this.loadData();
        });
    },
    onVesselVisibilityChange(evt, showOnInfoscreen) {
      if (!evt.added) return;

      var vessel = evt.added.element;
      var previousValue = vessel.show_on_infoscreen;
      vessel.show_on_infoscreen = showOnInfoscreen;

      axios
        .put("https://api.eines-as.no/vessels/" + vessel.id, {
          show_on_infoscreen: showOnInfoscreen,
        })
        .catch((error) => {
          console.log(error);
          vessel.show_on_infoscreen = previousValue;
          this.$bvToast.toast(
            "Kunne ikke oppdatere infoskjerm-visning for " + vessel.name + ". Laster inn på nytt...",
            {
              title: "Feil",
              variant: "danger",
              solid: true,
            }
          );
          this.loadData();
        });
    },
    openEditCrew(member) {
      if (this.isDisplayMode) return;
      this.crewEdit = Object.assign({}, member);
      this.$bvModal.show("crewOverviewEditCrew");
    },
    saveCrewEdit() {
      var member = this.crews.find((crew) => crew.id === this.crewEdit.id);
      if (!member) return;

      axios
        .put("https://api.eines-as.no/crews/" + this.crewEdit.id, {
          name: this.crewEdit.name,
          role: this.crewEdit.role,
          vessel: this.crewEdit.vessel ? this.crewEdit.vessel.id : null,
        })
        .then(() => {
          member.name = this.crewEdit.name;
          member.role = this.crewEdit.role;
          member.vessel = this.crewEdit.vessel;
          this.buildGroups();
          this.$bvModal.hide("crewOverviewEditCrew");
        })
        .catch((error) => {
          console.log(error);
          this.$bvToast.toast("Kunne ikke lagre mannskap.", {
            title: "Feil",
            variant: "danger",
            solid: true,
          });
        });
    },
    deleteCrewEdit() {
      axios
        .delete("https://api.eines-as.no/crews/" + this.crewEdit.id)
        .then(() => {
          this.crews = this.crews.filter((crew) => crew.id !== this.crewEdit.id);
          this.buildGroups();
          this.$bvModal.hide("crewOverviewEditCrew");
        })
        .catch((error) => {
          console.log(error);
          this.$bvToast.toast("Kunne ikke slette mannskap.", {
            title: "Feil",
            variant: "danger",
            solid: true,
          });
        });
    },
    openEditSkift(skift) {
      if (this.isDisplayMode) return;
      this.skiftEdit = Object.assign({}, skift);
      this.$bvModal.show("crewOverviewEditSkift");
    },
    saveSkiftEdit() {
      var skift = this.skifts.find((s) => s.id === this.skiftEdit.id);
      if (!skift) return;

      axios
        .put("https://api.eines-as.no/skift/" + this.skiftEdit.id, {
          name: this.skiftEdit.name,
          vessel: this.skiftEdit.vessel ? this.skiftEdit.vessel.id : null,
        })
        .then(() => {
          skift.name = this.skiftEdit.name;
          skift.vessel = this.skiftEdit.vessel;
          this.buildGroups();
          this.$bvModal.hide("crewOverviewEditSkift");
        })
        .catch((error) => {
          console.log(error);
          this.$bvToast.toast("Kunne ikke lagre skift.", {
            title: "Feil",
            variant: "danger",
            solid: true,
          });
        });
    },
    deleteSkiftEdit() {
      axios
        .delete("https://api.eines-as.no/skift/" + this.skiftEdit.id)
        .then(() => {
          this.skifts = this.skifts.filter((s) => s.id !== this.skiftEdit.id);
          this.buildGroups();
          this.$bvModal.hide("crewOverviewEditSkift");
        })
        .catch((error) => {
          console.log(error);
          this.$bvToast.toast("Kunne ikke slette skift.", {
            title: "Feil",
            variant: "danger",
            solid: true,
          });
        });
    },
    skiftMemberCount(skift) {
      return (this.skiftGroups[skift.id] || []).length;
    },
    isSkiftExpanded(skift) {
      return this.isDisplayMode || !!this.expandedSkifts[skift.id];
    },
    toggleSkift(skiftId) {
      this.$set(this.expandedSkifts, skiftId, !this.expandedSkifts[skiftId]);
    },
    toggleUnassignedCrew() {
      this.expandedUnassignedCrew = !this.expandedUnassignedCrew;
    },
    isCrewInSkiftEdit(member) {
      return !!(member.skift && this.skiftEdit && member.skift.id === this.skiftEdit.id);
    },
    toggleCrewSkiftEdit(member) {
      var previousSkift = member.skift;
      var newSkiftId = this.isCrewInSkiftEdit(member) ? null : this.skiftEdit.id;

      axios
        .put("https://api.eines-as.no/crews/" + member.id, { skift: newSkiftId })
        .then((response) => {
          member.skift = response.data.skift;
          this.buildGroups();
        })
        .catch((error) => {
          console.log(error);
          member.skift = previousSkift;
          this.$bvToast.toast("Kunne ikke oppdatere skift for " + member.name + ".", {
            title: "Feil",
            variant: "danger",
            solid: true,
          });
        });
    },
    roleVariant(role) {
      return this.roleVariants[role] || "light";
    },
    statusVariant(status) {
      return this.statusVariants[status] || "light";
    },
    tripDateTime(date, time) {
      if (!date) return null;
      return date + "T" + (time || "00:00");
    },
    formatStep(date, time) {
      var dateTime = this.tripDateTime(date, time);
      if (!dateTime) return "";
      return this.$date(dateTime).format("DD.MM.YYYY HH:mm");
    },
    toggleTrip(tripId) {
      this.$set(this.expandedTrips, tripId, !this.expandedTrips[tripId]);
    },
    tripDuration(trip) {
      var start = this.tripDateTime(
        trip.loading && trip.loading.arrivalDate,
        trip.loading && trip.loading.arrivalTime
      );
      if (!start) return null;

      var startMs = new Date(start).getTime();
      var endDateTime = this.tripDateTime(
        trip.unloading && trip.unloading.endDate,
        trip.unloading && trip.unloading.endTime
      );
      var endMs = endDateTime ? new Date(endDateTime).getTime() : this.now;

      var diffMs = Math.max(0, endMs - startMs);
      var totalSeconds = Math.floor(diffMs / 1000);
      var hours = Math.floor(totalSeconds / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;

      if (endDateTime) {
        return hours + "t " + minutes + "min";
      }
      return hours + "t " + minutes + "min " + seconds + "s";
    },
  },
  watch: {
    isDisplayMode: {
      immediate: true,
      handler(isDisplayMode) {
        document.body.classList.toggle("tv-mode", isDisplayMode);
      },
    },
  },
  created: function () {
    this.loadData();
  },
  mounted: function () {
    this.clockInterval = setInterval(() => {
      this.now = Date.now();
      this.checkAutoUpdateDue();
    }, 1000);

    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    document.addEventListener("visibilitychange", this.onVisibilityChange);

    if (this.isDisplayMode) {
      this.startAutoUpdate();
    }
  },
  beforeDestroy: function () {
    clearInterval(this.clockInterval);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    document.body.classList.remove("tv-mode");
  },
};
</script>

<style lang="css">
/* Both panes are capped to the viewport and scroll independently, so
   scrolling one never moves or resizes the other. */
.resource-pane,
.vessel-main-pane {
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

.tv-logo {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 1000;
  display: inline-block;
  text-decoration: none !important;
}

.tv-logo img {
  height: 64px;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.3));
}

.tv-controls {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 20px;
}

.tv-clock-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
}

.tv-clock-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.tv-clock {
  font-size: 2.5rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #495057;
}

.tv-last-updated {
  font-size: 0.9rem;
  color: #6c757d;
}

.tv-progress-wrap {
  position: relative;
  width: 72px;
  height: 72px;
  opacity: 0;
  transition: opacity 0.2s;
}

.tv-controls:hover .tv-progress-wrap,
.tv-controls:hover .tv-fullscreen-btn,
.tv-controls:hover .tv-back-btn,
.tv-controls:hover .tv-refresh-btn {
  opacity: 1;
}

.tv-fullscreen-btn,
.tv-back-btn,
.tv-refresh-btn {
  width: 64px;
  height: 64px;
  padding: 0;
  border-radius: 50%;
  font-size: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s;
}

.tv-progress-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 72px;
  height: 72px;
  transform: rotate(-90deg);
  pointer-events: none;
}

.tv-progress-ring-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.4);
  stroke-width: 4;
}

.tv-progress-ring-fg {
  fill: none;
  stroke: #fff;
  stroke-width: 4;
  stroke-linecap: round;
  transition: stroke-dashoffset 1s linear;
}

.tv-progress-wrap .btn {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 64px;
  height: 64px;
  padding: 0;
  border-radius: 50%;
  font-size: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

body.tv-mode .layout-sidenav,
body.tv-mode .layout-navbar {
  display: none !important;
}

body.tv-mode .layout-container {
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.crew-overview .card {
  border-radius: 0.5rem;
  overflow: hidden;
}

.resource-dropzone.is-empty {
  min-height: 35px;
}

.resource-dropzone:has(.sortable-ghost) {
  outline: 2px dotted #6c757d;
  outline-offset: 4px;
  border-radius: 4px;
  background-color: rgba(108, 117, 125, 0.06);
}

.dropzone-wrap {
  position: relative;
}

.vessel-divider {
  margin: 0.75rem 0;
}

.empty-placeholder {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  margin: 0;
  transform: translateY(-50%);
  text-align: center;
  pointer-events: none;
}

.resource-chip {
  background-color: #f8f9fa;
}

.entity-name-link {
  cursor: pointer;
}

.entity-name-link:hover {
  text-decoration: underline;
}

.member-card {
  cursor: pointer;
  background-color: #f8f9fa;
  transition: background-color 0.15s, border-color 0.15s;
}

.member-card:hover {
  background-color: #eef1f4;
}

.member-card-selected {
  background-color: #eaf6ec;
  border-color: #28a745 !important;
}

.drag-handle {
  cursor: grab;
  color: #adb5bd;
}

.skift-drag-handle {
  cursor: grab;
}

.skift-toggle {
  cursor: pointer;
  color: #adb5bd;
}

.skift-toggle:hover {
  color: #495057;
}

.skift-count-badge {
  background-color: #ced4da !important;
  color: #495057 !important;
}

.trip-toggle {
  cursor: pointer;
  user-select: none;
}

.trip-details div {
  line-height: 1.4;
}
</style>
